import { FC, Fragment, useEffect } from "react";
import { Input, Spin } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import ErrorBoundary from "modules/shared/components/ErrorBoundary";
import PageLoader from "modules/shared/components/PageLoader";
import { usePageTitle } from "modules/shared/hooks";
import { useOrganizationRoutes } from "routes/organizationRoute";
import { useProjectDetailsStore } from "modules/organization/store";
import { useRankingSnapshotStore } from "./store/rankingSnapshotStore";
import RankingSnapshotTableView from "./components/RankingSnapshotTableView";
import "./ranking-snapshot.css";

const { Search } = Input;

const OrgRankingSnapshotPage: FC = () => {
  const { ranking_project_id } = useParams<{ ranking_project_id: string }>();
  const { projectDetails, isLoadingProjectDetails } = useProjectDetailsStore();
  const { organizationBaseRouteUrl } = useOrganizationRoutes();
  const navigate = useNavigate();
  const {
    header,
    isLoading,
    searchTerm,
    getVisibleRows,
    getMedalSummary,
    fetchRankingSnapshot,
    setSearchTerm,
  } = useRankingSnapshotStore();

  usePageTitle(
    `MonQcle - ${
      projectDetails ? projectDetails?.title + " - Ranking Snapshot" : "Ranking Snapshot"
    }`,
  );

  useEffect(() => {
    if (!projectDetails?.slug || !ranking_project_id) {
      return;
    }
    fetchRankingSnapshot(projectDetails.slug, Number(ranking_project_id));
  }, [fetchRankingSnapshot, projectDetails?.slug, ranking_project_id]);

  return (
    <ErrorBoundary>
      <Fragment>
        {isLoadingProjectDetails ? (
          <PageLoader />
        ) : (
          <Spin spinning={isLoading}>
            {projectDetails && (
              <Fragment>
                <div className="pageTopSection">
                  <div className="backLink">
                    <Link
                      to={
                        organizationBaseRouteUrl +
                        `/project/${projectDetails?.slug}/ranking-projects`
                      }
                    >
                      <LeftOutlined role="img" aria-label="" />
                      Back to Ranking Projects
                    </Link>
                  </div>
                </div>
                <div className="pageHeadingWrap">
                  <h2 id="tab-header">Snapshot</h2>
                </div>
                <p className="snapshotProjectName">
                  {header?.ranking_project_title ?? ""}
                </p>
                <div className="snapshotLayoutWrap">
                  <div className="snapshotMainCol">
                    {header?.ranking_type === "template" && (
                      <div className="snapshotSummaryWrap">
                        {getMedalSummary().map((summary) => (
                          <div
                            className="snapshotSummaryCard"
                            key={summary.rank_name}
                          >
                            <span className="summaryLabel">
                              {summary.rank_name}
                            </span>
                            <span className="summaryCount">
                              {summary.count}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="tableSettingBlk">
                      <Search
                        aria-label="Search Ranking Snapshot"
                        placeholder="Search..."
                        allowClear
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <ErrorBoundary>
                      <RankingSnapshotTableView
                        rows={getVisibleRows()}
                        loading={isLoading}
                        rankingType={header?.ranking_type}
                        onOpenRecord={(record) =>
                          navigate(
                            organizationBaseRouteUrl +
                              `/project/${projectDetails?.slug}/record/${record.record_id}`,
                          )
                        }
                      />
                    </ErrorBoundary>
                  </div>
                  {/* Reserved layout space for a future Audit History panel — not built by this
                      feature (out of spec.md's written scope), per Story Owner direction to leave
                      room for it rather than have this page later require a layout change. */}
                  <div className="snapshotAsideCol">
                    <h3>Audit History</h3>
                  </div>
                </div>
              </Fragment>
            )}
          </Spin>
        )}
      </Fragment>
    </ErrorBoundary>
  );
};

export default OrgRankingSnapshotPage;
